import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 컨텐츠 애니메이션 변형 설정
const contentVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 버튼 애니메이션 변형 설정
const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.6,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 양수인 표시용 아파트명 (아파트 접미사 포함)
const ASSIGNEE_APARTMENT_MAP = {
  Richmond: "더리치먼드평창아파트",
  Raycounty: "레이카운티아파트",
  Harringtonplace: "해링턴플레이스GTX운정아파트",
  Singilpark: "신길파크자이아파트",
  DMCArtForest: "DMC아트포레자이아파트",
  GodeokUrbanBridge: "e편한세상고덕어반브릿지아파트",
  GeomdanUrbanCenter: "e편한세상검단어반센트로아파트",
  GeumgangPenteriumSeaglo: "금강펜테리움더시글로아파트",
  GeumgangPenteriumSeagloOffice: "아라역금강펜테리움더시글로1차오피스텔아파트",
  GangbyeonStarhills: "덕소강변스타힐스아파트",
  YangjuOkjeongLH: "양주옥정LH엘리프아파트",
  UlsanGwellCityXiComplex1: "울산지웰시티자이1단지아파트",
  YeongheungForestPrugioParkBien: "영흥숲푸르지오파크비엔아파트",
  HillstateYonginGojinStationComplex1: "힐스테이트용인고진역1단지아파트",
  HillstateYonginGojinStationComplex2: "힐스테이트용인고진역2단지아파트",
  HillstateBongdamPrideCity: "힐스테이트봉담프라이드시티아파트",
  PyeongchonIrvineFirstTheSharp: "평촌어바인퍼스트더샵아파트",
  NaepoNewTownMoaMiraedMegacity2nd: "내포신도시모아미래도메가시티2차아파트",
  BucheonIllumiState: "부천일루미스테이트아파트",
  SongdoStationSeohaeGrandbleThePark: "송도역서해그랑블더파크아파트",
  BupyeongHowstoryUrbanGreen: "부평하우스토리어반그린아파트",
  ForeonStation5: "포레온스테이션5", // 아파트 아님 — 접미사 없음
  HyundaiTerraTowerGuriGalmae: "현대테라타워구리갈매", // 아파트 아님 — 접미사 없음
  테스트: "테스트아파트",
};

const AssigneeApartmentValue = () => {
  const apartmentCode = Cookies.get("apartmentCode");
  const apartmentText = ASSIGNEE_APARTMENT_MAP[apartmentCode] || "";
  const orgLabel =
    apartmentCode === "ForeonStation5" ? "관리단" : "입주자 대표 회의";
  // 긴 아파트명도 한 줄로 유지 (글자 수에 따라 글자 크기만 조정)
  const fontSize = apartmentText.length > 14 ? "13px" : apartmentText.length > 11 ? "14px" : "16px";

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        color: "#1F2937",
        fontWeight: "600",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "2px",
        textAlign: "right",
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          lineHeight: "1.4",
          fontSize,
        }}
      >
        {apartmentText}
      </span>
      <span
        style={{
          whiteSpace: "nowrap",
          lineHeight: "1.4",
          fontSize: "16px",
        }}
      >
        {orgLabel}
      </span>
    </div>
  );
};

const SubmissionResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { building, unit, applicantName } = location.state || {};
  const [claimData, setClaimData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isShareListOpen, setIsShareListOpen] = useState(false);

  // 중복 호출 방지를 위한 ref
  const hasProcessedLogin = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (code && state === "submission_check" && !hasProcessedLogin.current) {
      hasProcessedLogin.current = true;
      handleKakaoSocialLogin(code);
    } else if (!code && !hasProcessedLogin.current) {
      // URL 파라미터가 없으면 바로 API 호출
      hasProcessedLogin.current = true;
      fetchClaimData();
    }
  }, [location.search]);

  // 카카오 소셜 로그인 처리
  const handleKakaoSocialLogin = async (code) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINTS.BASE_URL}/mobile/auth/kakao`,
        {
          // redirectUri: "http://localhost:3000/submission-result",
          redirectUri: "https://www.theh-agreement.co.kr/submission-result",
          code: code,
          // apartmentCode: "테스트",
          apartmentCode:
            Cookies.get("apartmentCode") === "Richmond"
              ? "리치먼드"
              : Cookies.get("apartmentCode") === "Raycounty"
                ? "레이카운티"
                : Cookies.get("apartmentCode") === "Harringtonplace"
                  ? "헤링턴플레이스"
                  : Cookies.get("apartmentCode") === "Singilpark"
                    ? "신길파크자이"
                    : Cookies.get("apartmentCode") === "DMCArtForest"
                      ? "DMC아트포레자이"
                      : Cookies.get("apartmentCode") === "GodeokUrbanBridge"
                        ? "고덕어반브릿지"
                        : Cookies.get("apartmentCode") === "GeomdanUrbanCenter"
                          ? "검단어반센트로"
                          : Cookies.get("apartmentCode") ===
                              "GeumgangPenteriumSeaglo"
                            ? "금강펜테리움더시글로"
                            : Cookies.get("apartmentCode") ===
                                "GeumgangPenteriumSeagloOffice"
                              ? "금강펜테리움더시글로오피스텔"
                              : Cookies.get("apartmentCode") ===
                                  "GangbyeonStarhills"
                                ? "덕소강변스타힐스"
                                : Cookies.get("apartmentCode") ===
                                    "YangjuOkjeongLH"
                                  ? "양주옥정LH엘리프"
                                  : Cookies.get("apartmentCode") ===
                                      "UlsanGwellCityXiComplex1"
                                    ? "울산지웰시티자이1단지"
                                    : Cookies.get("apartmentCode") ===
                                        "YeongheungForestPrugioParkBien"
                                      ? "영흥숲푸르지오파크비엔"
                                      : Cookies.get("apartmentCode") ===
                                          "HillstateYonginGojinStationComplex1"
                                        ? "힐스테이트용인고진역1단지"
                                        : Cookies.get("apartmentCode") ===
                                            "HillstateYonginGojinStationComplex2"
                                          ? "힐스테이트용인고진역2단지"
                                          : Cookies.get("apartmentCode") ===
                                              "HillstateBongdamPrideCity"
                                            ? "힐스테이트봉담프라이드시티"
                                            : Cookies.get("apartmentCode") ===
                                                "PyeongchonIrvineFirstTheSharp"
                                              ? "평촌어바인퍼스트더샵"
                                              : Cookies.get("apartmentCode") ===
                                                  "NaepoNewTownMoaMiraedMegacity2nd"
                                                ? "내포신도시모아미래도메가시티2차"
                                                : Cookies.get("apartmentCode") ===
                                                    "BucheonIllumiState"
                                                  ? "부천일루미스테이트"
                                                  : Cookies.get("apartmentCode") ===
                                                      "SongdoStationSeohaeGrandbleThePark"
                                                    ? "송도역서해그랑블더파크"
                                                    : Cookies.get("apartmentCode") ===
                                                        "BupyeongHowstoryUrbanGreen"
                                                      ? "부평하우스토리어반그린"
                                                      : Cookies.get("apartmentCode") ===
                                                          "ForeonStation5"
                                                        ? "포레온스테이션5"
                                                        : Cookies.get("apartmentCode") ===
                                                            "HyundaiTerraTowerGuriGalmae"
                                                          ? "현대테라타워구리갈매"
                                                          : Cookies.get("apartmentCode") ===
                                                              "테스트"
                                                            ? "테스트"
                                                            : "",
          email: "",
        },
      );

      // 로그인 성공 시 토큰과 사용자 정보를 쿠키에 저장
      if (response.data.accessToken && response.data.refreshToken) {
        // Access Token 저장
        Cookies.set(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
          response.data.accessToken,
          {
            expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          },
        );

        // Refresh Token 저장
        Cookies.set(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.REFRESH_TOKEN,
          response.data.refreshToken,
          {
            expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.REFRESH_TOKEN,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          },
        );

        // 사용자 정보 저장
        const userInfo = {
          userId: response.data.userId,
          status: response.data.status,
        };

        Cookies.set(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.USER_INFO,
          JSON.stringify(userInfo),
          {
            expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.USER_INFO,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          },
        );
      }

      // 로그인 성공 후 접수내역 조회
      fetchClaimData();
    } catch (error) {
      console.error("소셜 로그인 실패:", error);
      setLoginError("로그인에 실패했습니다. 다시 시도해주세요.");
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
        navigate("/");
      }, 2000);
      setIsLoading(false);
    }
  };

  // 접수내역 조회
  const fetchClaimData = async () => {
    try {
      // 쿠키에서 access token 가져오기
      const accessToken = Cookies.get(
        API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
      );

      if (!accessToken) {
        setError("로그인이 필요합니다.");
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      // 빈 객체인지 확인
      if (!response.data || Object.keys(response.data).length === 0) {
        setError("신청된 접수내역이 없습니다.");
        return;
      }

      setClaimData(response.data);
    } catch (error) {
      console.error("접수내역 조회 실패:", error);
      setError("접수내역을 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResubmit = () => {
    setShowDeleteModal(true);
  };

  // 신청서 삭제 함수
  const handleDeleteClaim = async () => {
    try {
      const accessToken = Cookies.get(
        API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
      );

      if (!accessToken) {
        alert("인증 토큰이 없습니다. 다시 로그인해주세요.");
        return;
      }

      await axios.delete(`${API_ENDPOINTS.BASE_URL}/mobile/claim`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      // 삭제 성공 시 홈페이지로 리다이렉트
      navigate("/");
    } catch (error) {
      console.error("신청서 삭제 실패:", error);
      alert("신청서 삭제에 실패했습니다. 다시 시도해주세요.");
    }
  };

  if (isLoading) {
    return (
      <motion.div
        className="submission-result-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        style={{
          height: "100vh",
          background: "#FFFFFF",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>접수내역을 불러오는 중...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="submission-result-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        style={{
          height: "100vh",
          background: "#FFFFFF",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <motion.div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            width: "90%",
            maxWidth: "360px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#1F2937",
                margin: "0 0 8px 0",
              }}
            >
              접수내역 없음
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6B7280",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              {error}
            </p>
          </div>
          <button
            onClick={() => navigate("/")}
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#7A4FFF",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            홈으로 돌아가기
          </button>
        </motion.div>
      </motion.div>
    );
  }

  // 실제 데이터에서 상태 결정
  const isWaitingForSignature =
    claimData?.sharedOwner && claimData?.claimStatus !== "complete";

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="page-container"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          padding: "0 0px",
          position: "relative",
          paddingBottom: "104px",
        }}
      >
        <motion.div
          style={{
            marginTop: "20px",
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
          }}
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.1,
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1],
            },
          }}
        >
          <button
            onClick={() => navigate("/")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 style={{ margin: 0, flex: 1, textAlign: "center" }}>접수내역</h1>
          <div style={{ width: "40px" }}></div>
        </motion.div>

        {isWaitingForSignature ? (
          // 전자서명 대기상태 화면
          <motion.div
            className="submission-waiting-content"
            variants={contentVariants}
            initial="initial"
            animate="animate"
            style={{
              width: "100%",
              maxWidth: "460px",
              paddingBottom: "100px",
              padding: "0 24px",
            }}
          >
            {/* 모래시계 아이콘 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <img
                src="/waiting2.png"
                alt="대기 중"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "contain",
                }}
              />
            </motion.div>

            {/* 대기 메시지 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              style={{
                textAlign: "center",
                marginBottom: "32px",
                padding: "0 8px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#1F2937",
                  margin: "0",
                  lineHeight: "1.4",
                }}
              >
                공동소유자의 전자서명을
                <br />
                기다리고 있습니다.
              </h2>
            </motion.div>

            {/* 주요 내용 요약 카드 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              style={{
                width: "100%",
                backgroundColor: "#F9FAFB",
                borderRadius: "12px",
                padding: "24px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1F2937",
                  margin: "0 0 16px 0",
                }}
              >
                주요 내용 요약
              </h3>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    아파트명
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                      flex: 1,
                      minWidth: 0,
                      textAlign: "right",
                      lineHeight: "1.4",
                      wordBreak: "keep-all",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {Cookies.get("apartmentCode") === "Richmond"
                      ? "더리치먼드평창"
                      : Cookies.get("apartmentCode") === "Raycounty"
                        ? "레이카운티"
                        : Cookies.get("apartmentCode") === "Harringtonplace"
                          ? "해링턴플레이스GTX운정"
                          : Cookies.get("apartmentCode") === "Singilpark"
                            ? "신길파크자이"
                            : Cookies.get("apartmentCode") === "DMCArtForest"
                              ? "DMC아트포레자이"
                              : Cookies.get("apartmentCode") ===
                                  "GodeokUrbanBridge"
                                ? "e편한세상고덕어반브릿지"
                                : Cookies.get("apartmentCode") ===
                                    "GeomdanUrbanCenter"
                                  ? "e편한세상검단어반센트로"
                                  : Cookies.get("apartmentCode") ===
                                      "GeumgangPenteriumSeaglo"
                                    ? "금강펜테리움더시글로"
                                    : Cookies.get("apartmentCode") ===
                                        "GeumgangPenteriumSeagloOffice"
                                      ? "아라역금강펜테리움더시글로1차오피스텔"
                                      : Cookies.get("apartmentCode") ===
                                          "GangbyeonStarhills"
                                        ? "덕소강변스타힐스"
                                        : Cookies.get("apartmentCode") ===
                                            "YangjuOkjeongLH"
                                          ? "양주옥정LH엘리프"
                                          : Cookies.get("apartmentCode") ===
                                              "UlsanGwellCityXiComplex1"
                                            ? "울산지웰시티자이1단지"
                                            : Cookies.get("apartmentCode") ===
                                                "YeongheungForestPrugioParkBien"
                                              ? "영흥숲푸르지오파크비엔"
                                              : Cookies.get("apartmentCode") ===
                                                  "HillstateYonginGojinStationComplex1"
                                                ? "힐스테이트용인고진역1단지"
                                                : Cookies.get(
                                                      "apartmentCode",
                                                    ) ===
                                                    "HillstateYonginGojinStationComplex2"
                                                  ? "힐스테이트용인고진역2단지"
                                                  : Cookies.get(
                                                        "apartmentCode",
                                                      ) ===
                                                      "HillstateBongdamPrideCity"
                                                    ? "힐스테이트봉담프라이드시티"
                                                    : Cookies.get(
                                                          "apartmentCode",
                                                        ) ===
                                                        "PyeongchonIrvineFirstTheSharp"
                                                      ? "평촌어바인퍼스트더샵"
                                                      : Cookies.get(
                                                            "apartmentCode",
                                                          ) ===
                                                          "NaepoNewTownMoaMiraedMegacity2nd"
                                                        ? "내포신도시모아미래도메가시티2차"
                                                        : Cookies.get(
                                                              "apartmentCode",
                                                            ) ===
                                                            "BucheonIllumiState"
                                                          ? "부천일루미스테이트"
                                                          : Cookies.get(
                                                                "apartmentCode",
                                                              ) ===
                                                              "SongdoStationSeohaeGrandbleThePark"
                                                            ? "송도역서해그랑블더파크"
                                                            : Cookies.get(
                                                                  "apartmentCode",
                                                                ) ===
                                                                "BupyeongHowstoryUrbanGreen"
                                                              ? "부평하우스토리어반그린"
                                                              : Cookies.get(
                                                                    "apartmentCode",
                                                                  ) ===
                                                                  "ForeonStation5"
                                                                ? "포레온스테이션5"
                                                                : Cookies.get(
                                                                      "apartmentCode",
                                                                    ) ===
                                                                    "HyundaiTerraTowerGuriGalmae"
                                                                  ? "현대테라타워구리갈매"
                                                                  : Cookies.get(
                                                                        "apartmentCode",
                                                                      ) === "테스트"
                                                                    ? "테스트아파트"
                                                                    : ""}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    동·호수
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {formatBuildingLabel(
                      claimData?.claimApartmentBlock || building || "102",
                      Cookies.get("apartmentCode"),
                    )}{" "}
                    {claimData?.claimApartmentUnit || unit || "0000"}호
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    소유자 성명
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.claimAssignmentName ||
                      applicantName ||
                      "김황해"}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    공동소유자 여부
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.sharedOwner ? "있음" : "없음"}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    양수인
                  </span>
                  <AssigneeApartmentValue />
                </div>
              </div>
            </motion.div>
            {/* 공동소유자 목록 카드 */}
            {claimData?.sharedOwner &&
              claimData?.claimShareList &&
              claimData.claimShareList.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.5 }}
                  style={{
                    width: "100%",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "12px",
                    padding: "24px",
                    marginBottom: "60px",

                    marginTop: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                      cursor: "pointer",
                    }}
                    onClick={() => setIsShareListOpen(!isShareListOpen)}
                  >
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#1F2937",
                        margin: 0,
                      }}
                    >
                      공동소유자 목록 ({claimData.claimShareList.length}명)
                    </h3>
                    <motion.div
                      animate={{ rotate: isShareListOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  </div>

                  <motion.div
                    initial={false}
                    animate={{
                      height: isShareListOpen ? "auto" : 0,
                      opacity: isShareListOpen ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {claimData.claimShareList.map((share, index) => (
                        <div
                          key={share.claimAssignmentShareId}
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "16px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#1F2937",
                              }}
                            >
                              {share.shareName}
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#6B7280",
                              }}
                            >
                              {index + 1}번째 공동소유자
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#6B7280",
                                }}
                              >
                                연락처
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#1F2937",
                                  fontWeight: "500",
                                }}
                              >
                                {share.sharePhone}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#6B7280",
                                }}
                              >
                                생년월일
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#1F2937",
                                  fontWeight: "500",
                                }}
                              >
                                {share.shareBirth}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#6B7280",
                                }}
                              >
                                동의 여부
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: share.claimAgree
                                    ? "#10B981"
                                    : "#EF4444",
                                  fontWeight: "500",
                                }}
                              >
                                {share.claimAgree ? "동의" : "미동의"}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#6B7280",
                                }}
                              >
                                신분증 제출
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: share.claimIdCard
                                    ? "#10B981"
                                    : "#EF4444",
                                  fontWeight: "500",
                                }}
                              >
                                {share.claimIdCard ? "완료" : "미완료"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              )}
          </motion.div>
        ) : (
          // 완료 상태 화면
          <motion.div
            className="submission-result-content"
            variants={contentVariants}
            initial="initial"
            animate="animate"
            style={{
              width: "100%",
              maxWidth: "460px",
              paddingBottom: "100px",
              padding: "0 24px",
            }}
          >
            {/* 헤더 섹션 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{
                width: "100%",
                marginBottom: "32px",
                padding: "0 8px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                채권양도·양수 완료
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  color: "#6B7280",
                  margin: "0",
                  lineHeight: "1.5",
                }}
              >
                {claimData?.claimAssignmentName || applicantName || "홍길동"}
                님의 채권양도·양수 처리가 완료되었습니다.
              </p>
            </motion.div>

            {/* 주요 내용 요약 카드 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              style={{
                width: "100%",
                backgroundColor: "#F9FAFB",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1F2937",
                  margin: "0 0 16px 0",
                }}
              >
                주요 내용 요약
              </h3>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    동·호수
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {formatBuildingLabel(
                      claimData?.claimApartmentBlock || building || "102",
                      Cookies.get("apartmentCode"),
                    )}{" "}
                    {claimData?.claimApartmentUnit || unit || "0000"}호
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    소유자 성명
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.claimAssignmentName ||
                      applicantName ||
                      "김황해"}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    공동 소유자 명
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.sharedOwner
                      ? (() => {
                          if (
                            claimData?.claimShareList &&
                            claimData.claimShareList.length > 0
                          ) {
                            const firstPerson = claimData.claimShareList[0];
                            const totalCount = claimData.claimShareList.length;

                            if (totalCount === 1) {
                              return firstPerson.shareName;
                            } else {
                              return `${firstPerson.shareName} 외${
                                totalCount - 1
                              }명`;
                            }
                          } else {
                            return (
                              claimData?.claimAssignmentName ||
                              "공동소유자 있음"
                            );
                          }
                        })()
                      : "-"}
                  </span>
                </div>

                {/* {claimData?.claimAssignmentName &&
                  claimData?.claimAssignmentName !== claimData?.claimName && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "16px",
                          color: "#6B7280",
                          fontWeight: "500",
                        }}
                      >
                        대리인
                      </span>
                      <span
                        style={{
                          fontSize: "16px",
                          color: "#1F2937",
                          fontWeight: "600",
                        }}
                      >
                        {claimData?.claimAssignmentName}
                      </span>
                    </div>
                  )} */}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    양수인
                  </span>
                  <AssigneeApartmentValue />
                </div>
              </div>
            </motion.div>

            {/* 처리 내역 카드 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              style={{
                width: "100%",
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "40px",
                border: "1px solid #F0F0F0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  paddingBottom: "16px",
                  borderBottom: "1px solid #F0F0F0",
                }}
              >
                <div>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1F2937",
                      margin: "0 0 4px 0",
                    }}
                  >
                    처리 내역
                  </h3>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    {claimData?.createdAt
                      ? new Date(claimData.createdAt).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "2025-03-26 15:30"}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "14px",
                    color: "#7A4FFF",
                    backgroundColor: "#F3F0FF",
                    padding: "6px 16px",
                    borderRadius: "6px",
                    fontWeight: "600",
                  }}
                >
                  완료
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    접수번호
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.claimAssignmentId || "접수번호"}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      fontWeight: "500",
                    }}
                  >
                    처리자
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1F2937",
                      fontWeight: "600",
                    }}
                  >
                    {claimData?.claimAssignmentName || "오주현"}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        <motion.div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "24px",
            background: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            width: "100%",
          }}
          variants={buttonVariants}
          initial="initial"
          animate="animate"
        >
          {/* GodeokUrbanBridge일 때만 표시되는 버튼 */}
          {Cookies.get("apartmentCode") === "GodeokUrbanBridge" && (
            <button
              onClick={() => {
                window.open(
                  "https://defect-litigation-yemige.vercel.app/v1",
                  "_blank",
                );
              }}
              style={{
                width: "100%",
                maxWidth: "460px",
                height: "56px",
                fontSize: "16px",
                fontWeight: "600",
                backgroundColor: "#FFFFFF",
                color: "#7A4FFF",
                border: "2px solid #7A4FFF",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#F5F3FF";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#FFFFFF";
              }}
            >
              하자조사 진행하기
            </button>
          )}

          {/* 접수 가능한 아파트일 때만 "다시 접수하기" 버튼 표시 */}
          {(() => {
            const apartmentCode = Cookies.get("apartmentCode");
            const isAcceptingApartment =
              apartmentCode === "GeomdanUrbanCenter" ||
              apartmentCode === "GeumgangPenteriumSeaglo" ||
              apartmentCode === "GeumgangPenteriumSeagloOffice" ||
              apartmentCode === "GangbyeonStarhills" ||
              apartmentCode === "UlsanGwellCityXiComplex1" ||
              apartmentCode === "YangjuOkjeongLH" ||
              apartmentCode === "PyeongchonIrvineFirstTheSharp" ||
              apartmentCode === "NaepoNewTownMoaMiraedMegacity2nd" ||
              apartmentCode === "BucheonIllumiState" ||
              apartmentCode === "SongdoStationSeohaeGrandbleThePark" ||
              apartmentCode === "BupyeongHowstoryUrbanGreen" ||
              apartmentCode === "ForeonStation5" ||
              apartmentCode === "테스트";

            return isAcceptingApartment ? (
              <button
                className={
                  isWaitingForSignature ? "confirm-button" : "confirm-button"
                }
                onClick={handleResubmit}
                style={{
                  width: "100%",
                  maxWidth: "460px",
                  height: "56px",
                  fontSize: "16px",
                  fontWeight: "600",
                  background: isWaitingForSignature ? "#EBEBFF" : "#EBEBFF",
                  color: isWaitingForSignature ? "#7A4FFF" : "#7A4FFF",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <>
                  {/* 새로고침 아이콘 */}
                  <img
                    src="/reset.png"
                    alt="다시 접수하기"
                    style={{
                      width: "20px",
                      height: "20px",
                      objectFit: "contain",
                    }}
                  />
                  다시 접수하기
                </>
              </button>
            ) : null;
          })()}
        </motion.div>
      </motion.div>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "360px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                신청서 삭제
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6B7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                기존 신청서를 삭제하고 새로 접수하시겠습니까?
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#F3F4F6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                취소
              </button>
              <button
                onClick={handleDeleteClaim}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#EF4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                삭제
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* 에러 모달 */}
      {showErrorModal && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="error-modal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <p>{loginError}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubmissionResultPage;
